// hooks/useCompass.js
// Cross-platform compass hook for iOS Safari and Android Chrome
// iOS: DeviceOrientationEvent.requestPermission() + event.webkitCompassHeading
// Android: deviceorientationabsolute event + 360 - event.alpha

import { useState, useEffect, useCallback, useRef } from "react";

const CARDINAL_DIRECTIONS = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];

const getCardinal = (bearing) => {
  if (bearing == null) return "";
  return CARDINAL_DIRECTIONS[Math.round(bearing / 45) % 8];
};

export const useCompass = (enabled = false) => {
  const [bearing, setBearing] = useState(null);
  const [permissionState, setPermissionState] = useState("unknown");
  const [error, setError] = useState(null);
  const listenerRef = useRef(null);
  const eventNameRef = useRef(null);

  // Detect platform capabilities on mount
  useEffect(() => {
    if (typeof DeviceOrientationEvent === "undefined") {
      setPermissionState("not_supported");
      return;
    }

    // iOS 13+ requires explicit permission request
    if (typeof DeviceOrientationEvent.requestPermission === "function") {
      setPermissionState("prompt");
    } else {
      // Android / other browsers - permission granted implicitly
      setPermissionState("granted");
    }
  }, []);

  // Handle orientation events
  const handleOrientation = useCallback((event) => {
    let heading = null;

    // iOS Safari provides webkitCompassHeading (0-360, 0 = North)
    if (event.webkitCompassHeading != null) {
      heading = event.webkitCompassHeading;
    }
    // Android Chrome: use alpha from absolute orientation
    else if (event.absolute && event.alpha != null) {
      heading = 360 - event.alpha;
    }
    // Fallback for non-absolute deviceorientation
    else if (event.alpha != null) {
      heading = 360 - event.alpha;
    }

    if (heading != null) {
      setBearing(Math.round(heading * 10) / 10);
    }
  }, []);

  // Request permission (must be called from a user gesture on iOS)
  const requestPermission = useCallback(async () => {
    if (typeof DeviceOrientationEvent === "undefined") {
      setPermissionState("not_supported");
      setError("Device orientation not supported");
      return false;
    }

    // iOS permission flow
    if (typeof DeviceOrientationEvent.requestPermission === "function") {
      try {
        const result = await DeviceOrientationEvent.requestPermission();
        if (result === "granted") {
          setPermissionState("granted");
          return true;
        } else {
          setPermissionState("denied");
          setError("Compass permission denied");
          return false;
        }
      } catch (e) {
        setPermissionState("denied");
        setError(e.message);
        return false;
      }
    }

    // Android - already granted
    setPermissionState("granted");
    return true;
  }, []);

  // Start/stop listening based on enabled + permission state
  useEffect(() => {
    if (!enabled || permissionState !== "granted") {
      // Clean up existing listener
      if (listenerRef.current && eventNameRef.current) {
        window.removeEventListener(eventNameRef.current, listenerRef.current);
        listenerRef.current = null;
        eventNameRef.current = null;
      }
      return;
    }

    listenerRef.current = handleOrientation;

    // Prefer absolute orientation (Android) if available
    if ("ondeviceorientationabsolute" in window) {
      eventNameRef.current = "deviceorientationabsolute";
    } else {
      eventNameRef.current = "deviceorientation";
    }

    window.addEventListener(eventNameRef.current, listenerRef.current);

    return () => {
      if (listenerRef.current && eventNameRef.current) {
        window.removeEventListener(eventNameRef.current, listenerRef.current);
        listenerRef.current = null;
        eventNameRef.current = null;
      }
    };
  }, [enabled, permissionState, handleOrientation]);

  return {
    bearing,
    cardinalDirection: getCardinal(bearing),
    permissionState,
    requestPermission,
    error,
  };
};
